import { Loader2, ShieldAlert } from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';

/**
 * The CO₂ tank rentals section of the audit portal is for office staff, not
 * drivers: it posts invoices to QuickBooks. AuditLayout admits drivers OR
 * superadmins; this narrows the rentals routes to superadmins only.
 */
export default function RentalsGate() {
  const { isSuperadmin, loading } = useIsSuperadmin();
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-brix-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Checking access…</span>
      </div>
    );
  }
  if (!isSuperadmin) {
    return (
      <div className="mx-auto mt-16 max-w-sm rounded-xl border border-brix-border bg-brix-surface p-6 text-center shadow-card">
        <ShieldAlert className="mx-auto h-8 w-8 text-brix-warning" />
        <h1 className="mt-3 font-display text-lg font-bold text-brix-ink">Office access required</h1>
        <p className="mt-2 text-sm text-brix-muted">Gas Cylinder Rental Fees management is for superadmins. Drivers audit counts from the customer list.</p>
      </div>
    );
  }
  return (
    <div>
      <RentalsNav />
      <Outlet />
    </div>
  );
}

const TABS: Array<{ to: string; label: string; match: (p: string) => boolean }> = [
  { to: '/audit/rentals', label: 'Customers', match: (p) => p === '/audit/rentals' || /^\/audit\/rentals\/\d+$/.test(p) },
  { to: '/audit/rentals/estimate', label: 'Estimate', match: (p) => p.startsWith('/audit/rentals/estimate') },
  { to: '/audit/rentals/run', label: 'This month’s invoices', match: (p) => p.startsWith('/audit/rentals/run') },
  { to: '/audit/rentals/audits', label: 'Audits & adjustments', match: (p) => p.startsWith('/audit/rentals/audits') },
];

/** The rentals section's own tabs — a customer page counts as "Customers". */
function RentalsNav() {
  const { pathname } = useLocation();
  return (
    <nav className="mb-4 flex flex-wrap gap-1 border-b border-brix-border text-sm">
      {TABS.map((t) => {
        const on = t.match(pathname);
        return (
          <Link
            key={t.to}
            to={t.to}
            className={`-mb-px border-b-2 px-3 py-2 ${on ? 'border-brix-primary font-semibold text-brix-ink' : 'border-transparent text-brix-muted hover:text-brix-ink'}`}
          >
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
}
